import React, { useState, useEffect } from "react";
import { FontLoader, C } from "./theme";
import { apiFetch, mapApiSession } from "./shared";
import { INIT_NOTIFS, CODES_RESIDENCE } from "./data";
import { I18N } from "./i18n";
import {
  INIT_RESIDENTS, INIT_COTISATIONS, INIT_CHARGES, INIT_TRAVAUX,
  INIT_PRESTATAIRES, INIT_FACTURES, INIT_EMPLOYES, INIT_DOCUMENTS, INIT_MAINTENANCE,
} from "./data";

import { AuthScreen }       from "./auth";
import { ResidentPortal }   from "./portals/ResidentPortal";
import { PrestatairPortal } from "./portals/PrestatairPortal";
import { AdminPortal }      from "./portals/AdminPortal";
import { Sidebar }          from "./components/Sidebar";
import { Dashboard }        from "./components/Dashboard";
import { ResidentsPage }    from "./components/ResidentsPage";
import { CotisationsPage }  from "./components/CotisationsPage";
import { FacturationPage }  from "./components/FacturationPage";
import { ChargesPage }      from "./components/ChargesPage";
import { TravauxPage }      from "./components/TravauxPage";
import { MaintenancePage }  from "./components/MaintenancePage";
import { EmployesPage }     from "./components/EmployesPage";
import { DocumentsPage }    from "./components/DocumentsPage";
import { MarketplacePage }  from "./components/MarketplacePage";
import { RapportsPage }     from "./components/RapportsPage";
import { AGPage }           from "./components/AGPage";

export default function App() {
  const [session, setSession]                       = useState(null);
  const [residents, setResidents]                   = useState(INIT_RESIDENTS);
  const [pendingResidents, setPendingResidents]     = useState([]);
  const [cotisations, setCotisations]               = useState(INIT_COTISATIONS);
  const [charges, setCharges]                       = useState(INIT_CHARGES);
  const [travaux, setTravaux]                       = useState(INIT_TRAVAUX);
  const [prestataires, setPrestataires]             = useState(INIT_PRESTATAIRES);
  const [employes, setEmployes]                     = useState(INIT_EMPLOYES);
  const [documents, setDocuments]                   = useState(INIT_DOCUMENTS);
  const [maintenance, setMaintenance]               = useState(INIT_MAINTENANCE);
  const [providerRequests, setProviderRequests]     = useState([]);
  const [notifs, setNotifs]                         = useState(INIT_NOTIFS);
  const [factures, setFactures]                     = useState(INIT_FACTURES);
  const [page, setPage]                             = useState("dashboard");
  const [immId, setImmId]                           = useState(1);
  const [locale, setLocale]                         = useState("fr");

  const logout = () => { setSession(null); setPage("dashboard"); };

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir  = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);

  const handleRegister = async (data) => {
    if (data.type === "provider") {
      setProviderRequests((c) => [...c, {
        id: Date.now(), companyName: data.companyName,
        firstName: data.firstName, lastName: data.lastName,
        email: data.email, phone: data.phone,
        serviceCategory: data.serviceCategory,
        description: data.description, password: data.password,
        status: "pending", createdAt: new Date().toISOString(),
      }]);
      return { message: (I18N[locale] || I18N.fr).providerSent };
    }

    try {
      const result = await apiFetch("/auth/register/coproprietaire", {
        method: "POST", headers: { "X-Locale": locale },
        body: JSON.stringify(data),
      });
      setPendingResidents((p) => [...p, {
        nom: data.lastName, prenom: data.firstName, email: data.email,
        tel: data.phone || "", apt: data.apartmentLabel, immId: 1,
        id: Date.now(), createdAt: new Date().toISOString().slice(0,10),
        statut: "En attente", tantiemes: 0, solde: 0,
      }]);
      return result;
    } catch (error) {
      const demo = CODES_RESIDENCE[data.invitationCode];
      if (!demo) throw error;
      setResidents((c) => [...c, {
        id: Date.now(), immId: demo.immId,
        nom: data.lastName, prenom: data.firstName,
        email: data.email, pass: data.password,
        tel: data.phone || "", apt: data.apartmentLabel,
        tantiemes: 0, solde: 0, statut: "OK",
      }]);
      return { message: (I18N[locale] || I18N.fr).ownerSubmitted, fallback: "demo" };
    }
  };

  const approveProviderRequest = (id) => {
    const req = providerRequests.find((r) => r.id === id);
    if (!req) return;
    setPrestataires((c) => [...c, {
      id: Date.now(), nom: req.companyName, cat: req.serviceCategory,
      note: 5, avis: 0, ville: "Casablanca", desc: req.description,
      tel: req.phone || "—", email: req.email, tarif: "Sur devis",
      certifié: true, pass: req.password, services: req.description,
      plan: "essai", trialDay: 1,
    }]);
    setProviderRequests((c) => c.filter((r) => r.id !== id));
  };

  const rejectProviderRequest = (id) =>
    setProviderRequests((c) => c.filter((r) => r.id !== id));

  const updateProviderProfile = (updated) => {
    setPrestataires((c) => c.map((p) => p.email === updated.email ? { ...p, ...updated } : p));
    setSession((s) => s?.role === "prestataire" ? { ...s, data: { ...s.data, ...updated } } : s);
  };

  if (!session) return (
    <><FontLoader />
      <AuthScreen onLogin={setSession} onRegister={handleRegister}
        residents={residents} prestataires={prestataires}
        locale={locale} onLocaleChange={setLocale} />
    </>
  );

  if (session.role === "resident") return (
    <><FontLoader />
      <ResidentPortal
        resident={residents.find(r => r.id === session.data.id) || session.data}
        cotisations={cotisations} charges={charges} travaux={travaux}
        factures={factures} onLogout={logout} />
    </>
  );

  if (session.role === "prestataire") return (
    <><FontLoader />
      <PrestatairPortal
        prest={prestataires.find((p) => p.email === session.data?.email) || session.data}
        onLogout={logout} onSaveProfile={updateProviderProfile} locale={locale} />
    </>
  );

  if (session.role === "admin") return (
    <><FontLoader />
      <AdminPortal locale={locale} providerRequests={providerRequests}
        residents={residents} prestataires={prestataires}
        onApproveProvider={approveProviderRequest}
        onRejectProvider={rejectProviderRequest}
        onLogout={logout} />
    </>
  );

  const totalPending = pendingResidents.filter(r => r.immId === immId).length;
  const maintUrgent  = maintenance.filter(
    m => m.immId === immId && m.statut !== "Résolu" && m.priorite === "critique"
  ).length;

  return (
    <><FontLoader />
      <div style={{ display:"flex", minHeight:"100vh", background: C.bg }}>
        <Sidebar page={page} setPage={setPage} onLogout={logout}
          immId={immId} setImmId={setImmId} pendingCount={totalPending}
          locale={locale} notifs={notifs} setNotifs={setNotifs}
          maintUrgent={maintUrgent} />
        <main style={{ marginLeft:238, flex:1, padding:"28px 32px", minHeight:"100vh" }}>
          {page==="dashboard"   && <Dashboard residents={residents} cotisations={cotisations} charges={charges} immId={immId} pendingCount={totalPending} setPage={setPage} locale={locale} maintenance={maintenance} />}
          {page==="residents"   && <ResidentsPage residents={residents} setResidents={setResidents} pendingResidents={pendingResidents} setPendingResidents={setPendingResidents} immId={immId} />}
          {page==="cotisations" && <CotisationsPage cotisations={cotisations} setCotisations={setCotisations} residents={residents} immId={immId} />}
          {page==="factures"    && <FacturationPage factures={factures} setFactures={setFactures} residents={residents} immId={immId} />}
          {page==="charges"     && <ChargesPage charges={charges} setCharges={setCharges} immId={immId} />}
          {page==="travaux"     && <TravauxPage travaux={travaux} setTravaux={setTravaux} immId={immId} />}
          {page==="maintenance" && <MaintenancePage maintenance={maintenance} setMaintenance={setMaintenance} immId={immId} residents={residents} prestataires={prestataires} />}
          {page==="employes"    && <EmployesPage employes={employes} setEmployes={setEmployes} immId={immId} />}
          {page==="documents"   && <DocumentsPage documents={documents} setDocuments={setDocuments} immId={immId} />}
          {page==="ag"          && <AGPage residents={residents} immId={immId} />}
          {page==="marketplace" && <MarketplacePage prestataires={prestataires} />}
          {page==="rapports"    && <RapportsPage residents={residents} cotisations={cotisations} charges={charges} immId={immId} />}
        </main>
      </div>
    </>
  );
}
